import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';
import { createSyllabusSubmission } from '../../../api';

let nextRecordingId = 1;

// Accept filter for the "Upload" button's file picker (browsing the device
// for an existing file), matched to whichever mode tab is active.
const ACCEPT_BY_MODE = { video: 'video/*', audio: 'audio/*', image: 'image/*' };

/**
 * `topic` + `sessionNumber` tie this panel's practice to a coach Syllabus
 * Tracker Topic + Session (e.g. topic="Action Songs", sessionNumber=1 for
 * "Roly Poly") so "Upload" actually reaches the coach's Syllabus Tracker
 * "Submissions" filter. When either is missing (a view with no matching
 * Syllabus Tracker topic, e.g. Balgopal Stories), Upload stays local-only,
 * exactly as before this was wired up.
 */
export default function PracticePanel({
  sceneId,
  topic,
  sessionNumber,
  description = 'Record yourself for this scene and upload it.',
}) {
  const { user } = useAuth();
  const isConnected = Boolean(topic && sessionNumber);
  const [mode, setMode] = useState('video'); // 'video' | 'audio' | 'image'
  const [isRecording, setIsRecording] = useState(false);
  const [recordings, setRecordings] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [submittingId, setSubmittingId] = useState(null);

  const videoPreviewRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const startedAtRef = useRef(null);
  const imageInputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Recordings are scene-specific practice, so switching scenes resets the working list.
  useEffect(() => {
    recordings.forEach((recording) => recording.url && URL.revokeObjectURL(recording.url));
    setRecordings([]);
    setExpandedId(null);
    setNoteDraft('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneId]);

  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach((track) => track.stop());
      recordings.forEach((recording) => recording.url && URL.revokeObjectURL(recording.url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = null;
    }
  };

  // The <video> preview only mounts once `isRecording` is true (it's inside
  // a conditional render), so attaching the stream has to happen here, after
  // that mount - not inside startRecording, where the ref is still null
  // because isRecording hasn't flipped yet at that point.
  useEffect(() => {
    if (mode === 'video' && isRecording && videoPreviewRef.current && streamRef.current) {
      videoPreviewRef.current.srcObject = streamRef.current;
    }
  }, [isRecording, mode]);

  const startRecording = async () => {
    try {
      const constraints = mode === 'video' ? { video: true, audio: true } : { audio: true };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      chunksRef.current = [];
      const recorder = new MediaRecorder(stream);
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const mimeType = mode === 'video' ? 'video/webm' : 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const url = URL.createObjectURL(blob);
        const durationSeconds = Math.max(1, Math.round((Date.now() - startedAtRef.current) / 1000));

        setRecordings((prev) => [
          {
            id: nextRecordingId++,
            kind: mode,
            url,
            blob,
            mimeType,
            fileName: `${mode}-${Date.now()}.webm`,
            durationSeconds,
            recordedAtLabel: new Date().toLocaleString(),
          },
          ...prev,
        ]);

        stopStream();
      };

      mediaRecorderRef.current = recorder;
      startedAtRef.current = Date.now();
      recorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Failed to start recording:', error);
      toast.error('Could not access camera/microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
  };

  const handleImageSelected = (event) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow re-selecting the same file
    if (!file) return;

    const url = URL.createObjectURL(file);
    setRecordings((prev) => [
      {
        id: nextRecordingId++,
        kind: 'image',
        url,
        blob: file,
        mimeType: file.type || 'image/jpeg',
        fileName: file.name || `photo-${Date.now()}.jpg`,
        durationSeconds: 0,
        recordedAtLabel: new Date().toLocaleString(),
      },
      ...prev,
    ]);
  };

  // "Upload" (the button, not the central record circle): pick an existing
  // file from the device instead of recording live. Just adds it to "Your
  // Recording" like a fresh recording would - it isn't sent to the coach
  // until "Save".
  const handleFileSelected = (event) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow re-selecting the same file
    if (!file) return;

    const url = URL.createObjectURL(file);
    setRecordings((prev) => [
      {
        id: nextRecordingId++,
        kind: mode,
        url,
        blob: file,
        mimeType: file.type || ACCEPT_BY_MODE[mode],
        fileName: file.name || `${mode}-${Date.now()}`,
        durationSeconds: 0,
        recordedAtLabel: new Date().toLocaleString(),
      },
      ...prev,
    ]);
  };

  const handlePickFile = () => {
    fileInputRef.current?.click();
  };

  const handleTapToRecord = () => {
    if (mode === 'image') {
      imageInputRef.current?.click();
      return;
    }
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const deleteRecording = (id) => {
    setRecordings((prev) => {
      const target = prev.find((recording) => recording.id === id);
      if (target?.url) URL.revokeObjectURL(target.url);
      return prev.filter((recording) => recording.id !== id);
    });
    if (expandedId === id) setExpandedId(null);
  };

  const togglePlay = (recording) => {
    setExpandedId((prev) => (prev === recording.id ? null : recording.id));
  };

  // "Save": submits the newest not-yet-submitted recording (live-recorded or
  // picked via "Upload") to the coach - this is the action that actually
  // sends it, not just a local keep. Once it's sent, it's the coach's copy
  // to grade, not a draft the student can still edit - so it's removed from
  // "Your Recording" (and its Delete button goes with it) rather than left
  // sitting there marked as sent.
  const handleSave = async () => {
    const target = recordings[0];
    if (!target) {
      toast('Record or upload something first!', { icon: '🎬' });
      return;
    }
    if (!isConnected) {
      toast('Saving isn’t connected yet — kept locally for now.', { icon: '✅' });
      return;
    }

    setSubmittingId(target.id);
    try {
      const formData = new FormData();
      formData.append('topic', topic);
      formData.append('sessionNumber', String(sessionNumber));
      formData.append('submissionType', target.kind);
      if (noteDraft.trim()) formData.append('note', noteDraft.trim());
      formData.append('file', target.blob, target.fileName);

      await createSyllabusSubmission(formData);

      if (target.url) URL.revokeObjectURL(target.url);
      setRecordings((prev) => prev.filter((r) => r.id !== target.id));
      if (expandedId === target.id) setExpandedId(null);
      setNoteDraft('');
      toast.success('Sent to your coach for review!');
    } catch (error) {
      console.error('Failed to save practice submission:', error);
      toast.error(error?.response?.data?.message || 'Could not save. Please try again.');
    } finally {
      setSubmittingId(null);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const displayName = user?.name || 'You';
  const initial = displayName.charAt(0).toUpperCase();

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex flex-col gap-4 h-fit">
      <div>
        <h3 className="text-2xl font-extrabold text-slate-900">Your Practice</h3>
        <p className="text-sm font-medium text-gray-500">{description}</p>
      </div>

      {/* Mode toggle */}
      <div className="flex gap-2">
        <button
          onClick={() => !isRecording && setMode('video')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-extrabold transition-colors ${
            mode === 'video' ? 'bg-violet-600 text-white shadow-sm' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
        >
          🎥 Video
        </button>
        <button
          onClick={() => !isRecording && setMode('audio')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-extrabold transition-colors ${
            mode === 'audio' ? 'bg-violet-600 text-white shadow-sm' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
        >
          🎙️ Audio
        </button>
        <button
          onClick={() => !isRecording && setMode('image')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-extrabold transition-colors ${
            mode === 'image' ? 'bg-violet-600 text-white shadow-sm' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
        >
          📷 Photo
        </button>
      </div>

      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleImageSelected}
      />

      {/* "Upload" button's file picker: browse the device for an existing
          file (no `capture`, unlike imageInputRef above, so it opens the
          normal file browser instead of jumping straight to the camera). */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_BY_MODE[mode]}
        className="hidden"
        onChange={handleFileSelected}
      />

      {/* Recorder */}
      <div className="rounded-2xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-3 py-9 px-4 bg-gray-50">
        {mode === 'video' && isRecording && (
          <video ref={videoPreviewRef} autoPlay muted playsInline className="w-full max-w-xs rounded-xl mb-2" />
        )}
        <button
          onClick={handleTapToRecord}
          className={`h-[72px] w-[72px] rounded-full flex items-center justify-center transition-colors ${
            isRecording ? 'bg-red-100 animate-pulse' : 'bg-red-100'
          }`}
          aria-label={mode === 'image' ? 'Upload a photo' : isRecording ? 'Stop recording' : 'Start recording'}
        >
          <span className="h-12 w-12 rounded-full bg-red-500 text-white flex items-center justify-center text-xl shadow-md">
            {mode === 'video' ? '🎥' : mode === 'audio' ? '🎤' : '📷'}
          </span>
        </button>
        <span className="text-[15px] font-bold text-slate-700">
          {mode === 'image'
            ? 'Tap to Upload a Photo'
            : isRecording
              ? 'Recording… tap to stop'
              : `Tap to Record ${mode === 'video' ? 'Video' : 'Audio'}`}
        </span>
      </div>

      {/* Recordings list */}
      <div>
        <p className="text-[15px] font-extrabold text-slate-800 mb-2">Your Recording</p>
        <div className="flex flex-col gap-2">
          {recordings.length === 0 && (
            <p className="rounded-xl border border-dashed border-gray-200 p-3 text-xs font-medium text-gray-400">
              No recordings yet — record or upload one above.
            </p>
          )}
          {recordings.map((recording) => (
            <div key={recording.id} className="rounded-xl border border-gray-100 p-3">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 shrink-0 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center font-extrabold overflow-hidden">
                  {initial}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-extrabold text-slate-900 truncate">{displayName}</p>
                  <p className="text-xs font-medium text-gray-400">
                    {recording.recordedAtLabel}
                    {recording.kind !== 'image' ? ` · ${formatDuration(recording.durationSeconds)}` : ''}
                  </p>
                </div>
                <button
                  onClick={() => togglePlay(recording)}
                  className="flex items-center gap-1 text-xs font-extrabold text-white bg-blue-500 hover:bg-blue-600 rounded-lg px-3 py-2"
                >
                  {recording.kind === 'image' ? '🖼 View' : '▶ Play'}
                </button>
                <button
                  onClick={() => deleteRecording(recording.id)}
                  className="flex items-center gap-1 text-xs font-extrabold text-red-600 bg-white border-2 border-red-200 hover:bg-red-50 rounded-lg px-3 py-2"
                >
                  🗑 Delete
                </button>
              </div>
              {expandedId === recording.id && recording.url && (
                <div className="mt-3">
                  {recording.kind === 'video' && <video src={recording.url} controls className="w-full rounded-lg" />}
                  {recording.kind === 'audio' && <audio src={recording.url} controls className="w-full" />}
                  {recording.kind === 'image' && (
                    <img src={recording.url} alt="Practice submission" className="w-full rounded-lg" />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {isConnected && (
        <div>
          <label htmlFor="practice-note" className="text-xs font-bold text-slate-600 block mb-1">
            Add a note for your coach (optional)
          </label>
          <textarea
            id="practice-note"
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            rows={2}
            maxLength={500}
            placeholder="e.g. I practiced the actions twice!"
            className="w-full rounded-xl border border-gray-200 p-2 text-sm"
          />
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 pt-1">
        <button
          onClick={handlePickFile}
          className="rounded-xl bg-blue-50 text-blue-700 font-extrabold py-3 hover:bg-blue-100"
        >
          ☁️ Upload
        </button>
        <button
          onClick={handleSave}
          disabled={submittingId !== null}
          className="rounded-xl bg-green-600 text-white font-extrabold py-3 hover:bg-green-700 disabled:opacity-60"
        >
          {submittingId !== null ? '✅ Saving…' : '✅ Save'}
        </button>
      </div>
    </div>
  );
}
